import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bell, Check, CheckCheck, Trash2 } from 'lucide-react';

const TONE = {
  info:    'bg-primary-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  error:   'bg-danger-500',
};

const relativeTime = (value) => {
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * In-app notification bell.
 *
 * Rows come from services/notifications.js, which writes an in-app copy for every
 * rent reminder, payment receipt and maintenance update it dispatches. The bell
 * previously rendered a hardcoded red dot and did nothing when clicked.
 */
const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/notifications?limit=20');
      setItems(data.notifications || []);
      setUnread(Number(data.unread) || 0);
    } catch {
      /* the bell must never break the shell */
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) { setLoading(true); await load(); setLoading(false); }
  };

  const markRead = async (id) => {
    setItems((xs) => xs.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try { await axios.put(`/api/notifications/${id}/read`); } catch { load(); }
  };

  const markAll = async () => {
    setItems((xs) => xs.map((n) => ({ ...n, is_read: 1 })));
    setUnread(0);
    try { await axios.put('/api/notifications/read-all'); } catch { load(); }
  };

  const remove = async (id, wasUnread) => {
    setItems((xs) => xs.filter((n) => n.id !== id));
    if (wasUnread) setUnread((u) => Math.max(0, u - 1));
    try { await axios.delete(`/api/notifications/${id}`); } catch { load(); }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        className="relative rounded-xl p-2 text-neutral-500 transition-colors hover:bg-neutral-100/80 hover:text-neutral-900"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 flex h-4 min-w-[1rem] items-center justify-center">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-danger-400 opacity-75" />
            <span className="relative flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-gradient-to-r from-danger-500 to-danger-600 px-1 text-[10px] font-bold leading-none text-white shadow-xs">
              {unread > 9 ? '9+' : unread}
            </span>
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[22rem] max-w-[calc(100vw-1.5rem)] overflow-hidden card shadow-xl ring-1 ring-black/5 animate-slide-down">
          <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-3 bg-neutral-50/50">
            <p className="text-sm font-bold text-neutral-900 tracking-tight">
              Notifications{unread > 0 && <span className="ml-1.5 text-xs font-semibold text-primary-700 bg-primary-50 px-1.5 py-0.5 rounded-full">({unread} new)</span>}
            </p>
            {unread > 0 && (
              <button type="button" onClick={markAll}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-800">
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[24rem] overflow-y-auto">
            {loading && items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-neutral-500">Loading…</p>
            ) : items.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="mx-auto h-6 w-6 text-neutral-300" />
                <p className="mt-2 text-sm font-medium text-neutral-700">You're all caught up</p>
                <p className="mt-1 text-xs text-neutral-500">
                  Rent reminders, receipts and maintenance updates appear here.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {items.map((n) => (
                  <li key={n.id} className={`group flex gap-3 px-4 py-3 ${n.is_read ? '' : 'bg-primary-50/40'}`}>
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.is_read ? 'bg-neutral-300' : (TONE[n.type] || TONE.info)}`} />
                    <div className="min-w-0 flex-1">
                      <p className={`truncate text-sm ${n.is_read ? 'text-neutral-700' : 'font-medium text-neutral-900'}`}>
                        {n.title}
                      </p>
                      <p className="mt-0.5 line-clamp-2 text-xs text-neutral-600">{n.message}</p>
                      <p className="mt-1 text-[11px] text-neutral-400">{relativeTime(n.created_at)}</p>
                    </div>
                    <div className="flex shrink-0 flex-col gap-1 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
                      {!n.is_read && (
                        <button type="button" onClick={() => markRead(n.id)} title="Mark as read"
                          className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-primary-700">
                          <Check className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button type="button" onClick={() => remove(n.id, !n.is_read)} title="Remove"
                        className="rounded p-1 text-neutral-400 hover:bg-danger-50 hover:text-danger-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
